import { Phone, MessageCircle, Mail, MapPin, Clock, ArrowUpRight } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { useT } from "@/context/ContentContext";
import PageHead from "@/components/PageHead";

export default function Contactos() {
    const { settings, whatsappLink } = useSettings();
    const t = useT();
    const [form, setForm] = useState({ name: "", phone: "", email: "", message: "", rgpd: false });
    const [sending, setSending] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (!form.rgpd) return toast.error("Aceite a política de privacidade");
        setSending(true);
        try {
            await api.post("/leads", {
                name: form.name, phone: form.phone, email: form.email, location: "-",
                property_type: "Outro", work_types: [], description: form.message,
                timing: "", budget: "", file_ids: [], rgpd_consent: true,
            });
            toast.success("Mensagem enviada. Entraremos em contacto brevemente.");
            setForm({ name: "", phone: "", email: "", message: "", rgpd: false });
        } catch { toast.error("Erro ao enviar. Tente novamente."); }
        finally { setSending(false); }
    };

    return (
        <>
            <PageHead
                title="Contactos — O Homem da Obra"
                description="Fale connosco por telefone, WhatsApp ou email. Atendimento em Lisboa, Cascais, Oeiras, Sintra e distritos de Setúbal e Santarém."
                canonical="https://ohomemdaobra.pt/contactos"
            />
            <section className="py-14 sm:py-20">
                <div className="max-w-4xl mx-auto px-6 sm:px-10">
                    <div className="eyebrow mb-6"><Phone className="w-3.5 h-3.5" /> {t("contactos.eyebrow", "Contactos")}</div>
                    <h1 className="font-display text-4xl sm:text-6xl lg:text-7xl font-bold text-stone-900 tracking-tight leading-[1.02]">
                        {t("contactos.title", "Falamos consigo.")}
                    </h1>
                </div>
            </section>

            <section className="pb-20">
                <div className="max-w-6xl mx-auto px-6 sm:px-10 grid lg:grid-cols-2 gap-8">
                    <div className="space-y-3">
                        <Contact icon={Phone} title="Telefone" value={settings.phone} href={`tel:${settings.phone.replace(/\s/g,'')}`} testid="contact-phone" />
                        <Contact icon={MessageCircle} title="WhatsApp" value="Enviar mensagem" href={whatsappLink()} target="_blank" testid="contact-whatsapp" />
                        <Contact icon={Mail} title="Email" value={settings.email} href={`mailto:${settings.email}`} testid="contact-email" />
                        <Contact icon={MapPin} title="Área de atuação" value={settings.coverage_area} testid="contact-area" />
                        <Contact icon={Clock} title="Horário" value={settings.hours} testid="contact-hours" />
                        <p className="text-sm text-stone-600 leading-relaxed px-1 pt-3" data-testid="contactos-client-note">
                            {t("contactos.client_note", "Já é cliente? O ponto de situação da sua obra está na sua Área do Cliente, no link que recebeu por email.")}
                        </p>
                    </div>

                    <form onSubmit={submit} className="card-stone p-8 sm:p-10 space-y-4" data-testid="contact-form">
                        <h2 className="font-display text-2xl font-bold text-stone-900 mb-2">{t("contactos.form.title", "Envie-nos uma mensagem")}</h2>
                    <p className="text-sm text-stone-600 leading-relaxed mb-4">
                        {t("contactos.form.subtitle", "Diga-nos o que precisa e respondemos pelo contacto que indicar.")}
                    </p>
                        <input required placeholder="Nome" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} className={inpc} data-testid="cf-name" />
                        <input required type="tel" placeholder="Telefone" value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} className={inpc} data-testid="cf-phone" />
                        <input required type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} className={inpc} data-testid="cf-email" />
                        <textarea required rows={5} placeholder="Mensagem" value={form.message} onChange={(e) => setForm({...form, message: e.target.value})} className={inpc} data-testid="cf-message" />
                        <label className="flex items-start gap-2 text-sm text-stone-600">
                            <input type="checkbox" checked={form.rgpd} onChange={(e) => setForm({...form, rgpd: e.target.checked})} className="mt-1 accent-[hsl(var(--moss))]" data-testid="cf-rgpd" />
                            <span>Aceito a política de privacidade.</span>
                        </label>
                        <button type="submit" disabled={sending} className="btn-moss w-full justify-center" data-testid="cf-submit">
                            {sending ? "A enviar…" : <>Enviar mensagem <span className="arrow-circle"><ArrowUpRight className="w-3.5 h-3.5" /></span></>}
                        </button>
                    </form>
                </div>
            </section>
        </>
    );
}

const inpc = "w-full px-4 py-3 rounded-2xl border border-[hsl(var(--border))] bg-white focus:ring-2 focus:ring-[hsl(var(--moss))] focus:outline-none";
function Contact({ icon: Icon, title, value, href, target, testid }) {
    const inner = (
        <div className="card-stone flex items-start gap-4 card-stone-hover">
            <div className="w-12 h-12 rounded-2xl bg-[hsl(var(--moss))] text-white flex items-center justify-center flex-shrink-0">
                <Icon className="w-5 h-5" />
            </div>
            <div>
                <div className="text-xs font-medium uppercase tracking-wider text-[hsl(var(--moss))] mb-1">{title}</div>
                <div className="text-lg font-medium text-stone-900">{value}</div>
            </div>
        </div>
    );
    if (href) return <a href={href} target={target} rel={target ? "noopener noreferrer" : undefined} className="block" data-testid={testid}>{inner}</a>;
    return <div data-testid={testid}>{inner}</div>;
}
